import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import MuiDialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";

import { withTranslation } from "react-i18next";
import { isEmpty, isNil } from "lodash";

import fpCommonService from "@plannel/services/fp/fp-common-service";
import FpGridSetting from "@plannel/pages/factory-plan/common/grid";
import NumberUtil from "@plannel/utils/number-util";
import dateUtils from "@plannel/utils/date-util";
import debug from "@plannel/utils/debug-util";

/**
 * 작업 주문 조회/선택 전용 팝업 — old `WorkOrderPopup.jsx`(NOBUTTONS, 더블클릭 선택)와 동일하게
 * 선택 전용(관리 기능 없음). 더블클릭 시 선택 행의 `woCd`만 onSelect로 돌려준 뒤 닫힌다.
 *
 * props: open, onClose(), planScope, routeCd, onSelect({woCd})
 */
const WorkOrderPopup = ({ t, open, onClose, planScope, routeCd, onSelect }) => {
  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);
  const gridOptions = useMemo(() => FpGridSetting({ title: t("FP_WORK_ORDER_SELECT"), viewName: "" }), [t]);

  // 선택 전용 팝업이라 전 컬럼 editable:false 명시 — FpGridSetting의 defaultColDef.editable(true)
  // 상속으로 더블클릭 시 편집이 열리는 걸 막는다(InventoryPopup과 동일 이유). 폭은 old 비율 유지 + §3-2 소폭 확장.
  const columnDefs = useMemo(() => ([
    { headerName: t("FP_SO_CD"), field: "dmndCd", colId: "dmndCd", width: 160, editable: false },
    { headerName: t("FP_WORK_ORDER_CODE"), field: "woCd", colId: "woCd", width: 160, editable: false },
    { headerName: t("FP_INVENTORY_CD"), field: "inventoryCd", colId: "inventoryCd", width: 160, editable: false },
    { headerName: t("FP_INVENTORY_NAME"), field: "inventoryNm", colId: "inventoryNm", width: 220, editable: false },
    { headerName: t("FP_ITEM_CD"), field: "itemCd", colId: "itemCd", width: 160, editable: false },
    { headerName: t("FP_ITEM_NM"), field: "itemNm", colId: "itemNm", width: 220, editable: false },
    // old numberFormat "#,##0.#####" 대응.
    { headerName: t("FP_REQUEST_QTY"), field: "requestQty", colId: "requestQty", width: 110, editable: false,
      valueFormatter: p => (isNil(p.value) || p.value === "" || !isFinite(p.value)) ? "" : NumberUtil.formatNumberData(p, { maximumFractionDigits: 5, minimumFractionDigits: 0 }) },
    { headerName: t("FP_DUE_DT"), field: "dueDate", colId: "dueDate", width: 150, editable: false, valueFormatter: dateUtils.formatDateTime }
  ]), [t]);

  const loadWorkOrders = () => {
    if (!planScope) return;
    // old도 호출 셀의 routeCd를 함께 보낸다. 다만 WorkOrderQueryRepository.makeWhereClause에는
    // routeCd 조건이 없어 old/new 모두 실질적으로 무시된다(계획범위 전체 목록). 백엔드가 조건을
    // 추가하면 그대로 걸리도록 old와 동일하게 계속 보낸다.
    fpCommonService.getWorkOrders({ planScope, routeCd: routeCd ?? "" })
      .then(response => setRowData(isEmpty(response.data) ? [] : response.data))
      .catch(e => { debug(e); setRowData([]); });
  };

  useEffect(() => {
    if (!open || !planScope) return;
    loadWorkOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, planScope, routeCd]);

  // old NOBUTTONS와 동일하게 더블클릭으로만 확정 — 반환은 woCd 하나뿐(old setWoValues와 동일).
  const onRowDoubleClicked = (e) => {
    if (!e.data) return;
    onSelect({ woCd: e.data.woCd });
    onClose();
  };

  return (
    <MuiDialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>{t("FP_WORK_ORDER_SELECT")}</DialogTitle>
      <DialogContent>
        <Box className="ag-theme-balham" sx={{ height: 440, width: "100%" }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            onRowDoubleClicked={onRowDoubleClicked}
            {...gridOptions}
            rowSelection={{ mode: "singleRow", checkboxes: false, enableClickSelection: true }}
          />
        </Box>
      </DialogContent>
    </MuiDialog>
  );
};

export default withTranslation()(WorkOrderPopup);
